<template>
    <details @toggle="toggleDays">
        <summary>
            <div class="weeks-container flexible-container" :class="{'border-bottom': isLast, 'is-expanded': isExpanded}">
                <div class="bold-font">{{ t('projectclient.activitytracker.week') }} {{ weekNumber }}</div>
                <div class="weeks-container__chevron-wrapper">
                    <div class="days-worked-wrapper" :style="{background: dayWorkAmountBg}">{{ dayWorkAmount }}</div>
                    <VJoyIcon v-if="!showDays" :name="'chevron-down'" size="xxsmall" color="neutral" />
                    <VJoyIcon v-if="showDays" :name="'chevron-up'" size="xxsmall" color="neutral" />
                </div>
            </div>
        </summary>
        <div
            v-for="(item, index) in week"
            :key="index"
            class="days-container flexible-container"
            :class="{'border-bottom': index === week.length - 1 && isLast}"
        >
            <DetailedActivityTrackerDay :weekday="item.weekday" :date="item.date" :work-amount="item.workAmount" />
        </div>
    </details>
</template>
<script setup lang="ts">
    import {ref, computed} from 'vue';
    import {useTranslation} from '#imports';
    import {VJoyIcon} from '@maltjoy/core-vue';
    import type {WorkedUnit} from '@api';
    import DetailedActivityTrackerDay from './DetailedActivityTrackerDay.vue';
    import type {WorkWeek} from '../../../types';
    import {useFormatTotalText} from '../../../utils';

    const {t} = useTranslation();

    const props = defineProps<{
        weekNumber: number;
        week: WorkWeek[];
        isLast: boolean;
        workedUnit: WorkedUnit;
    }>();

    const totalDayWorkAmount = computed<number>(() => {
        return props.week.reduce((total, day) => total + day.workAmount, 0);
    });

    const dayWorkAmount = useFormatTotalText(totalDayWorkAmount.value, props.workedUnit);

    const showDays = ref(false);
    const isExpanded = ref(false);
    const toggleDays = (event: any) => {
        showDays.value = !showDays.value;
        isExpanded.value = event.target.open;
    };

    const dayWorkAmountBg = computed<string>(() => {
        return totalDayWorkAmount.value > 0 ? 'var(--joy-color-information-10)' : 'var(--joy-color-quaternary-10)';
    });
</script>
<style lang="scss" scoped>
    ::marker {
        content: '';
    }

    .weeks-container {
        border: 1px solid var(--joy-color-neutral-30);
        border-bottom: none;
        font-size: var(--joy-font-size-primary-300);

        &.is-expanded {
            border-bottom: 1px solid var(--joy-color-neutral-30);
            border-bottom-left-radius: 0;
            border-bottom-right-radius: 0;
        }

        &__chevron-wrapper {
            display: flex;
            align-items: center;
        }
    }

    .days-container {
        border-bottom: 1px solid var(--joy-color-neutral-30);
        border-left: 1px solid var(--joy-color-neutral-30);
        border-right: 1px solid var(--joy-color-neutral-30);

        &:first-child {
            border-top: 1px solid var(--joy-color-neutral-30) !important;
        }

        &:last-child {
            border-bottom: none;
        }
    }

    .border-bottom {
        border-bottom: 1px solid var(--joy-color-neutral-30) !important;
        border-bottom-left-radius: var(--joy-core-radius-2);
        border-bottom-right-radius: var(--joy-core-radius-2);
    }

    .bold-font {
        font-weight: var(--joy-font-weight-bold);
    }

    .days-worked-wrapper {
        display: inline-block;
        padding: var(--joy-core-spacing-1) var(--joy-core-spacing-2);
        border-radius: 25px;
        text-align: center;
        margin-left: var(--joy-core-spacing-1);
        margin-right: var(--joy-core-spacing-4);
    }
</style>
